import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { FlightCard, Sectors } from '@/src/types/flight'
import { isEmpty } from '@/src/utils/generalUtils'
import { ExtractedFlightCardProps } from '@/src/utils/srp/flightCardProps'
import { getFlightData, getLayoversForFlight } from '@/src/utils/srp/flightResultsUtil'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { useFormatTime } from '../../formatTime'
import useSearchResults from '../../useSearchResults'

export const useGetRTSolutionCardData = (card: FlightCard) => {
    const formatTime = useFormatTime()
    const { formatCurrency } = useFormatCurrencyForSrp()
    const tCommon = useTranslations('common')
    const { jsons, sectors, selectedJourneyLeg } = useSearchResults()
    function extractFlightCardProps (flight: FlightCard, sectors: Sectors) {
        return {
            layovers: getLayoversForFlight(flight, sectors),
            seatsLeft: (flight as FlightCard).indicators?.seatsLeft,
            noBaggage: (flight as FlightCard).indicators?.noBaggage,
            longLayover: (flight as FlightCard).indicators?.longLayover,
            coupon_detail: (flight as FlightCard).priceBreakup?.fare?.coupon_detail,
            arrivalDate: (flight as FlightCard)?.lastArrival?.date || '',
            departureDate: (flight as FlightCard)?.firstDeparture?.date || ''
        }
    }
    const flightData: ExtractedFlightCardProps = {
        ...getFlightData({
            flight: card,
            airline_names: jsons?.airline_names || {},
            sectors: sectors || {},
            isRtSolution: true,
            translationsFn: tCommon
        }),
        ...extractFlightCardProps(card, sectors || {})
    }

    const priceNote = selectedJourneyLeg === 0 ? tCommon('roundTripTotalPrice') : null
    const pricePrefix = selectedJourneyLeg === 1 ? '+' : null
    const couponDetails = useMemo(() => {
        if (
            selectedJourneyLeg === 0 &&
            !isEmpty(flightData.coupon_detail) &&
            flightData.coupon_detail?.discountAmount
        ) {
            const discountedPrice = flightData.price - flightData.coupon_detail?.discountAmount

            if (discountedPrice > 0 && discountedPrice < flightData.price) {
                return {
                    ...flightData.coupon_detail,
                    discountedPrice: formatCurrency(discountedPrice)
                }
            }
        }

        return null
    }, [selectedJourneyLeg, flightData.coupon_detail, flightData.price])

    const strikeOffPrice = useMemo(() => {
        if (typeof flightData.strikeOffPrice === 'number' && flightData.strikeOffPrice > Number(flightData.price)) {
            return flightData.strikeOffPrice
        }
        return null
    }, [flightData.strikeOffPrice, flightData.price])

    const departureAirportName = useMemo(() => {
        return jsons?.ap?.[flightData.departureAirport?.toUpperCase()]?.n
    }, [flightData.departureAirport, jsons])
    const arrivalAirportName = useMemo(() => {
        return jsons?.ap?.[flightData.arrivalAirport?.toUpperCase()]?.n || ''
    }, [flightData.arrivalAirport, jsons])

    return {
        ...flightData,
        duration: formatTime(flightData.totalDuration),
        isNextDayCallOut: flightData.departureDate !== flightData.arrivalDate,
        priceNote,
        pricePrefix,
        couponDetails,
        strikeOffPrice,
        departureAirportName,
        arrivalAirportName
    }
}
